import { Dialog } from "@mui/material";
import { useCallback, useState } from "react";
import { ReportAccessType } from "@entrilia/shared/api/reportAccessTypes.ts";
import { logError } from "@entrilia/shared/logging.ts";
import type { ReportInfo, ReportUsage } from "../../../../../../api/types/svTypes.ts";
import { useGroupsContext } from "../../groups/contexts/GroupsContext.ts";
import { CopyToDialogCopying } from "./CopyToDialogCopying";
import { CopyToDialogSelection } from "./CopyToDialogSelection";
import { ReportOverridePrompt } from "./ReportOverridePrompt";
import { getAllNonExistedGroups } from "./hooks/getAllNonExistedGroups";
import useReportValidation from "./hooks/useReportValidation";

export interface Props {
  reports: ReportInfo[];
  reportUsage: ReportUsage;
  onClose: (success?: boolean) => void;
}

export default function CopyToDialog({ reports, reportUsage, onClose }: Props) {
  const { actions: groupActions } = useGroupsContext();
  const reportValidation = useReportValidation(reports);
  const [showSecondScreen, setShowSecondScreen] = useState(false);
  const [groupsCreating, setGroupsCreating] = useState(false);
  const [showReportOverridePrompt, setShowReportOverridePrompt] = useState(false);
  const [copyConditionValues, setCopyConditionValues] = useState(true);
  const [override, setOverride] = useState(false);
  const [copyAccess, setCopyAccess] = useState(ReportAccessType.FullAccess);
  const [inheritFromParent, setInheritFromParent] = useState(false);

  const startCopying = useCallback(async () => {
    setShowSecondScreen(true);

    const groupsToCreate = getAllNonExistedGroups(reportValidation.selectedCompanies);
    if (groupsToCreate.length === 0) {
      return;
    }

    setGroupsCreating(true);
    try {
      await groupActions.addMany?.(groupsToCreate);
    } catch (e) {
      logError(e, "[CopyToDialog] createGroups");
    } finally {
      setGroupsCreating(false);
    }
  }, [groupActions, reportValidation.selectedCompanies]);

  const handleStartCopy = useCallback(() => {
    const anyOfReportWithExistName = reportValidation.selectedCompanies.some((c) =>
      c.reports.some((r) => r.result?.name?.exist === true)
    );

    if (anyOfReportWithExistName) {
      setShowReportOverridePrompt(true);
    } else {
      startCopying();
    }
  }, [reportValidation.selectedCompanies, startCopying]);

  const handleContinueCopy = useCallback(
    (value: boolean) => {
      setOverride(value);
      setShowReportOverridePrompt(false);
      startCopying();
    },
    [startCopying]
  );

  return (
    <>
      <Dialog open fullWidth slotProps={{ paper: { sx: { height: "800px", maxWidth: "970px" } } }}>
        {!showSecondScreen && (
          <CopyToDialogSelection
            reports={reports}
            reportValidation={reportValidation}
            copyConditionValues={copyConditionValues}
            copyAccess={copyAccess}
            inheritFromParent={inheritFromParent}
            onCopy={handleStartCopy}
            onClose={onClose}
            onCopyConditionValues={setCopyConditionValues}
            onCopyAccess={setCopyAccess}
            onInheritFromParent={setInheritFromParent}
          />
        )}
        {showSecondScreen && (
          <CopyToDialogCopying
            reports={reports}
            selectedCompanies={reportValidation.selectedCompanies}
            copyConditionValues={copyConditionValues}
            copyAccess={copyAccess}
            inheritFromParent={inheritFromParent}
            override={override}
            groupsCreating={groupsCreating}
            onClose={onClose}
            reportUsage={reportUsage}
          />
        )}
        {showReportOverridePrompt && (
          <ReportOverridePrompt onContinue={handleContinueCopy} onCancel={() => setShowReportOverridePrompt(false)} />
        )}
      </Dialog>
    </>
  );
}
